import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Cpu, Globe2, Plane, Building2, Compass, ArrowUpRight,
  Network, ShieldCheck, GraduationCap, Sparkles, MapPin,
} from "lucide-react";
import Hero from "../components/Hero";
import SolutionCard from "../components/SolutionCard";
import LeadForm from "../components/LeadForm";
import SectionHeading from "../components/SectionHeading";
import api from "../lib/api";

const SOLUTIONS = [
  {
    eyebrow: "School OS",
    title: "Digital School Ecosystem",
    description:
      "End-to-end digital infrastructure for modern institutions — LMS, AI assessments, dashboards, and secure communication unified into one operating system.",
    bullets: ["LMS + extracurricular", "AI proctored exams", "Parent & teacher dashboards"],
    icon: Cpu,
    to: "/school-os",
    testId: "solution-card-school-os",
  },
  {
    eyebrow: "Event & MUN OS",
    title: "Academic Event Operating System",
    description:
      "Host, manage, and scale Model United Nations, conferences, and youth parliaments with delegate workflows, AI evaluations, and certification.",
    bullets: ["Delegate management", "Live moderation", "AI evaluations & analytics"],
    icon: Globe2,
    to: "/event-os",
    testId: "solution-card-event-os",
  },
  {
    eyebrow: "Educational Tourism",
    title: "Global Exposure Programs",
    description:
      "Experiential learning beyond classrooms — academic immersion, cultural exchange, and industry visits engineered for curricular outcomes.",
    bullets: ["University immersions", "Industrial visits", "Cultural exchanges"],
    icon: GraduationCap,
    to: "/educational-tourism",
    testId: "solution-card-educational-tourism",
  },
  {
    eyebrow: "Institutional Travel",
    title: "Corporate & Academic Delegations",
    description:
      "Premium end-to-end coordination for academic delegations, conference travel, and institutional movement across continents.",
    bullets: ["Flights, hotels, transfers", "Visa & insurance", "On-ground support"],
    icon: Plane,
    to: "/corporate-travel",
    testId: "solution-card-corporate-travel",
  },
  {
    eyebrow: "Destination Management",
    title: "On-Ground Execution Partner",
    description:
      "Logistics, group handling, and event execution across the UAE and global destinations — institutional travel without operational gaps.",
    bullets: ["Logistics", "Event execution", "Local coordination"],
    icon: Compass,
    to: "/destination-management",
    testId: "solution-card-destination-management",
  },
];

const VALUES = [
  {
    icon: Network,
    title: "Architectural Thinking",
    text: "We don't sell tools — we engineer integrated ecosystems with governance, identity, data, and learning unified.",
  },
  {
    icon: ShieldCheck,
    title: "Institutional Trust",
    text: "Built for governments, councils, and academic bodies with the rigor, discretion, and continuity such relationships demand.",
  },
  {
    icon: Globe2,
    title: "Global Reach",
    text: "UAE-rooted, globally engaged. Delivered across the Gulf, Asia, Europe, and Africa with consistent execution.",
  },
  {
    icon: Sparkles,
    title: "Premium Execution",
    text: "Every engagement is treated like a flagship project. No shortcuts. No noise. Outcomes that defend themselves.",
  },
];

export default function HomePage() {
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setInsights((r.data || []).slice(0, 3)))
      .catch(() => setInsights([]));
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />

      {/* About */}
      <section
        data-testid="home-about"
        className="relative bg-[#F8FAFC] py-24 sm:py-28"
      >
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                About Destineze
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-navy">
                A UAE-based partner engineered for global institutions.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-3">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                Destineze Educational Voyages FZCO is a global education and
                institutional solutions company. We build the digital
                ecosystems schools and universities run on, the platforms
                governments and councils host their events on, and the global
                exposure programs that shape the next generation of leaders.
              </p>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                Our work is institutional-first — designed for governance,
                continuity, and outcomes that withstand scrutiny.
              </p>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
                {VALUES.map((v) => (
                  <div key={v.title}>
                    <div className="h-9 w-9 bg-navy text-gold flex items-center justify-center">
                      <v.icon size={18} strokeWidth={1.5} />
                    </div>
                    <div className="mt-3 font-display text-base text-navy">
                      {v.title}
                    </div>
                    <div className="mt-1 text-sm text-slate-600 leading-relaxed">
                      {v.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Solutions */}
      <section
        data-testid="home-solutions"
        className="relative bg-white py-24 sm:py-28"
      >
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <SectionHeading
              eyebrow="Core Solutions"
              title="One global company. Five integrated capabilities."
              description="Each solution is built to stand on its own, and engineered to compound when combined."
            />
            <Link
              to="/partners"
              data-testid="solutions-partner-link"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-navy hover:text-gold-500 transition"
            >
              Become a partner <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {SOLUTIONS.slice(0, 2).map((s, i) => (
              <SolutionCard key={s.title} {...s} index={i + 1} className="lg:col-span-1" />
            ))}
            <SolutionCard
              {...SOLUTIONS[2]}
              index={3}
              variant="dark"
              className="lg:col-span-1"
            />
            <SolutionCard {...SOLUTIONS[3]} index={4} className="lg:col-span-1" />
            <SolutionCard {...SOLUTIONS[4]} index={5} className="lg:col-span-2" />
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section
        data-testid="home-global-reach"
        className="relative bg-navy text-white overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
                Global Reach
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
                Headquartered in Dubai. <br />
                Engaged across continents.
              </h2>
              <p className="mt-6 text-white/70 text-base leading-relaxed max-w-md">
                From Gulf institutions to European universities and African
                councils, our work crosses borders — through digital products,
                academic events, and on-ground execution.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {[
                  ["Gulf · UAE", "Regional headquarters"],
                  ["Asia Pacific", "India · Singapore · ASEAN"],
                  ["Europe", "Cross-continental engagements"],
                ].map(([region, sub]) => (
                  <div
                    key={region}
                    className="bg-navy p-7 hover:bg-navy-700 transition group"
                  >
                    <div className="flex items-center gap-2 text-gold">
                      <MapPin size={14} />
                      <span className="text-xs uppercase tracking-[0.22em]">
                        {region}
                      </span>
                    </div>
                    <div className="mt-4 font-display text-xl text-white">
                      {sub}
                    </div>
                    <div className="mt-3 text-sm text-white/60 group-hover:text-white/80 transition">
                      Institutions, councils, and partner networks engaged.
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials section removed — will be added later */}

      {/* ===== Diplomexa × Destineze ===== */}
      <section
        data-testid="home-diplomexa"
        className="relative bg-white py-24 sm:py-28 overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-light opacity-50 pointer-events-none" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 px-4 py-2 border border-gold-500/40 bg-white text-[11px] uppercase tracking-[0.28em] text-gold-500">
                <Sparkles size={12} /> Strategic Platform Partner
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-navy">
                Diplomexa <span className="text-gold-500">×</span> Destineze
              </h2>
              <div className="mt-4 text-base sm:text-lg text-slate-600 font-display">
                Global Conferences, MUNs, Debates &amp; Educational Leadership Journeys.
              </div>
              <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl">
                Through Diplomexa, Destineze enables schools, colleges, universities,
                and student groups to participate in structured global leadership
                experiences, MUN conferences, international debates, youth parliament
                programmes, and educational travel-linked academic events.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/diplomexa"
                  data-testid="home-diplomexa-explore"
                  className="group inline-flex items-center gap-2 bg-navy text-white px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-navy-700 transition"
                >
                  Explore Diplomexa
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
                <a
                  href="https://diplomexa.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-diplomexa-visit"
                  className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition"
                >
                  Visit diplomexa.ai
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="aspect-[4/3] bg-navy overflow-hidden border border-slate-200">
                  <img
                    src="https://images.pexels.com/photos/20733081/pexels-photo-20733081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Global student conference"
                    className="w-full h-full object-cover opacity-70"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
                </div>

                <div className="absolute -bottom-6 -left-6 sm:-left-10 bg-white border border-slate-200 shadow-2xl p-5 flex items-center gap-4 max-w-[300px]">
                  <img
                    src="/diplomexa-logo.png"
                    alt="Diplomexa"
                    data-testid="home-diplomexa-logo"
                    className="h-14 w-14 object-contain"
                  />
                  <div>
                    <div className="font-display text-lg text-navy">Diplomexa</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500 mt-1">
                      Conference OS · MUN OS
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insights preview */}
      {insights.length ? (
        <section
          data-testid="home-insights"
          className="relative bg-[#F8FAFC] py-24 sm:py-28"
        >
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <SectionHeading
                eyebrow="Insights"
                title="Editorial perspectives from the institutional frontier."
                description="Long-form thinking on the operating models reshaping global education."
              />
              <Link
                to="/insights"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-navy hover:text-gold-500 transition"
              >
                All insights <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
              {insights.map((p) => (
                <Link
                  to={`/insights/${p.slug}`}
                  key={p.id}
                  data-testid={`insight-card-${p.slug}`}
                  className="card-lift group block bg-white border border-slate-200"
                >
                  {p.cover_image ? (
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                      />
                    </div>
                  ) : null}
                  <div className="p-7">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
                      {p.category}
                    </div>
                    <h3 className="mt-3 font-display text-xl leading-tight text-navy">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 line-clamp-3">
                      {p.excerpt}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-navy">
                      Read insight <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Inquiry / partner band */}
      <section
        data-testid="home-inquiry"
        className="relative bg-white py-24 sm:py-28"
      >
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Engage"
              title="Build the future of education with us."
              description="Whether you lead an institution, a council, a ministry, or a corporate education function — start a structured conversation with our team."
            />
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {[
                ["Schools & Colleges", "Onboard School OS, host events, plan exposure programmes."],
                ["Universities", "Conferences, exchange programmes, institutional travel."],
                ["Governments & Councils", "Large-scale platforms, white-label deployments."],
                ["Corporates", "Education-led travel, delegations, leadership exposure."],
              ].map(([t, d]) => (
                <div key={t} className="border border-slate-200 p-5">
                  <div className="flex items-center gap-2 text-gold-500">
                    <Building2 size={14} />
                    <span className="text-[11px] uppercase tracking-[0.22em]">
                      {t}
                    </span>
                  </div>
                  <div className="mt-2 text-slate-700 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              inquiryType="institutional"
              testIdPrefix="home-lead-form"
              title="Start an institutional conversation"
              subtitle="Share your context. Our partnerships team responds within one business day."
            />
          </div>
        </div>
      </section>
    </main>
  );
}
